import React from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Flex, Spinner } from "@chakra-ui/react";
import { useAuth } from "../context/AuthContext";
import LoginPage from "../pages/LoginPage";
import ResetPasswordPage from "../pages/ResetPasswordPage";
import PricingFeatures from "../PricingFeatures";
import ClockInClockOut from "../pages/ClockInClockOut";
import NotFound from "../components/NotFound";
import AdminPanel from "../components/AdminPanel";
import MyProfile from "../pages/MyProfile";
import Ticketing from "../pages/Ticketing.js";
import TicketDetailPage from "../pages/TicketDetailPage";
import CreateTicketPage from "../pages/CreateTicketPage";
import Settings from "../pages/Settings";
import SidebarManagement from "../pages/SidebarManagement";
import ComingSoon from "../pages/ComingSoon.jsx";
import EmployeeDatabase from "../modules/employee/components/EmployeeDatabase";
import EmployeeFormPage from "../modules/employee/pages/EmployeeFormPage";
import DigitalFiles from "../modules/employee/components/DigitalFiles";
import RBAC from "../modules/rbac/components/RBAC";
import JobRequisitions from "../modules/recruitment/components/JobRequisitions";
import JobRequisitionForm from "../modules/recruitment/components/JobRequisitionForm";
import JobRequisitionDetailPage from "../modules/recruitment/components/JobRequisitionDetailPage";
import JobRequisitionEditPage from "../modules/recruitment/components/JobRequisitionEditPage";
import SourcingIntegrations from "../modules/recruitment/components/SourcingIntegrations";
import ATSDashboard from "../modules/recruitment/components/ATSDashboard";
import ApplicationManagement from "../modules/recruitment/components/ApplicationManagement";
import ApplicationDetails from "../modules/recruitment/components/ApplicationDetails";
import ApplicationReview from "../modules/recruitment/components/ApplicationReview.js";
import Offers from "../pages/Offers";
import OfferAcceptancePage from "../pages/OfferAcceptancePage";
import OfferApprovalsPage from "../pages/OfferApprovalsPage";
import EmailApprovalPage from "../pages/EmailApprovalPage";
import PreBoardingDashboard from "../modules/recruitment/components/PreBoardingDashboard";
import DocumentVerificationPage from "../modules/recruitment/components/DocumentVerificationPage";
import CandidatePortal from "../modules/recruitment/components/CandidatePortal";
import SetupPassword from "../pages/SetupPassword";
import Dashboard from "../pages/Dashboard.js";
import PageTransition from "../components/PageTransition";
import PayrollPage from "../pages/PayrollPage";
import CompensationAssignmentDetailPage from "../pages/CompensationAssignmentDetailPage";
import SalaryStructureDetailPage from "../pages/SalaryStructureDetailPage";
import AnalyticsPage from "../pages/AnalyticsPage";
import PlaceholderPage from "../components/PlaceholderPage";
import DevicesList from "../pages/attendance/DevicesList";
import DeviceDetails from "../pages/attendance/DeviceDetails";
import AttendancePage from "../modules/attendance/AttendancePage";
import LeaveRequest from "../modules/leave/LeaveRequest";
import LeaveApproval from "../modules/leave/LeaveApproval";

// Procurement Module Pages
import RequestList from "../modules/procurement/pages/RequestList";
import RequestCreate from "../modules/procurement/pages/RequestCreate";
import RequestEdit from "../modules/procurement/pages/RequestEdit";
import RequestDetail from "../modules/procurement/pages/RequestDetail";
import ApprovalList from "../modules/procurement/pages/ApprovalList";
import WorkflowList from "../modules/procurement/pages/WorkflowList";
import WorkflowCreate from "../modules/procurement/pages/WorkflowCreate";
import WorkflowEdit from "../modules/procurement/pages/WorkflowEdit";
import VendorList from "../modules/procurement/pages/VendorList";
import InterOfficeMemo from "../modules/procurement/pages/InterOfficeMemo";
import OrganizationalStructureMaintenance from "../modules/procurement/pages/OrganizationalStructureMaintenance";
import TerminalLogs from "../pages/TerminalLogs";

import TechNotifications from "../pages/TechNotifications";

// EDOS Module Pages
import ProjectsPage from "../pages/ProjectsPage";
import ProjectCreatePage from "../pages/ProjectCreatePage";
import ProjectDetailPage from "../pages/ProjectDetailPage";
import SprintsPage from "../pages/SprintsPage";
import SprintCreatePage from "../pages/SprintCreatePage";
import SprintDetailPage from "../pages/SprintDetailPage";
import EDOSDashboardsPage from "../pages/EDOSDashboardsPage";
import MilestonesPage from "../pages/MilestonesPage";
import MilestoneCreatePage from "../pages/MilestoneCreatePage";
import MilestoneDetailPage from "../pages/MilestoneDetailPage";
import ReleasesPage from "../pages/ReleasesPage";
import ReleaseCreatePage from "../pages/ReleaseCreatePage";
import ReleaseDetailPage from "../pages/ReleaseDetailPage";
import TimesheetsPage from "../pages/TimesheetsPage";
import TimesheetCreatePage from "../pages/TimesheetCreatePage";
import ResourcesPage from "../pages/ResourcesPage";
import PortfoliosPage from "../pages/PortfoliosPage";
import ProgramsPage from "../pages/ProgramsPage";
import AutomationsPage from "../pages/AutomationsPage";

// const ProtectedRoute = ({ children }) => {
//   const { loggedInUser, isLoading } = useUser();

//   if (isLoading) {
//     return (
//       <Flex justify="center" align="center" height="100vh">
//         <Spinner size="xl" color="teal.500" />
//       </Flex>
//     );
//   }
//   console.log("loggedInUser", loggedInUser);

//   // return loggedInUser ? children : <Navigate to="/login" />;
//   return loggedInUser ? children : null;
// };

const AppRoutes = () => {
  const navigate = useNavigate();
  const { isLoading: authIsLoading, isAuthenticated } = useAuth();
  const [isLoggedIn, setIsLoggedIn] = React.useState(
    Boolean(
      typeof window !== "undefined" &&
      (localStorage.getItem("token") || sessionStorage.getItem("token")),
    ),
  );

  React.useEffect(() => {
    // Listen for logout event (triggered by API interceptor on 401)
    const handleLogout = () => {
      console.log("🔐 Logout event detected - updating routes");
      setIsLoggedIn(false);
      // Use a small delay to ensure state is updated before navigation
      setTimeout(() => {
        console.log("🔐 Navigating to login...");
        navigate("/login", { replace: true });
      }, 100);
    };

    // Also check for token changes periodically
    const checkTokenStatus = () => {
      const hasToken = Boolean(
        localStorage.getItem("token") || sessionStorage.getItem("token"),
      );
      if (!hasToken && isLoggedIn) {
        console.log("🔐 Token removed from storage - logging out");
        setIsLoggedIn(false);
        setTimeout(() => {
          console.log("🔐 Navigating to login from token check...");
          navigate("/login", { replace: true });
        }, 100);
      } else if (hasToken && !isLoggedIn) {
        setIsLoggedIn(true);
      }
    };

    const handleLogin = () => {
      setIsLoggedIn(true);
    };

    console.log("🔐 AppRoutes effect setup - adding listeners");
    window.addEventListener("logout", handleLogout);
    window.addEventListener("login", handleLogin);
    const interval = setInterval(checkTokenStatus, 500);

    return () => {
      window.removeEventListener("logout", handleLogout);
      window.removeEventListener("login", handleLogin);
      clearInterval(interval);
    };
  }, [navigate, isLoggedIn]);

  // Show loading screen while AuthContext is loading auth state
  if (authIsLoading) {
    return (
      <Flex
        justify="center"
        align="center"
        minHeight="100vh"
        bg="white"
        _dark={{ bg: "gray.900" }}
      >
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="xl"
        />
      </Flex>
    );
  }

  if (!isLoggedIn) {
    return (
      <Routes>
        <Route
          path="/login"
          element={
            <PageTransition>
              <LoginPage />
            </PageTransition>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PageTransition>
              <ResetPasswordPage />
            </PageTransition>
          }
        />
        <Route
          path="/pricing"
          element={
            <PageTransition>
              <PricingFeatures />
            </PageTransition>
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route
        path="/MyProfile"
        element={
          <PageTransition>
            <MyProfile />
          </PageTransition>
        }
      />

      {/* Public routes (no authentication required) */}
      <Route
        path="/pricing"
        element={
          <PageTransition>
            <PricingFeatures />
          </PageTransition>
        }
      />
      <Route
        path="/offers/accept/:token"
        element={
          <PageTransition>
            <OfferAcceptancePage />
          </PageTransition>
        }
      />
      <Route
        path="/offers/approvals/:action/:token"
        element={
          <PageTransition>
            <EmailApprovalPage />
          </PageTransition>
        }
      />
      <Route
        path="/setup-password"
        element={
          <PageTransition>
            <SetupPassword />
          </PageTransition>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PageTransition>
            <ResetPasswordPage />
          </PageTransition>
        }
      />

      {/* ✅ Admin layout wrapper */}
      <Route
        path="/"
        element={
          // <ProtectedRoute>
          <AdminPanel />
          // </ProtectedRoute>
        }
      >
        {/* ✅ These go inside <Outlet /> */}
        <Route
          index
          element={
            <PageTransition>
              <Dashboard />
            </PageTransition>
          }
        />
        <Route
          path="Dashboard"
          element={
            <PageTransition>
              <Dashboard />
            </PageTransition>
          }
        />

        <Route
          path="Ticketing"
          element={
            <PageTransition>
              <Ticketing />
            </PageTransition>
          }
        />
        <Route
          path="Ticketing/create"
          element={
            <PageTransition>
              <CreateTicketPage />
            </PageTransition>
          }
        />
        <Route
          path="Ticketing/:id"
          element={
            <PageTransition>
              <TicketDetailPage />
            </PageTransition>
          }
        />
        <Route
          path="settings"
          element={
            <PageTransition>
              <Settings />
            </PageTransition>
          }
        />
        <Route
          path="settings/sidebar-management"
          element={
            <PageTransition>
              <SidebarManagement />
            </PageTransition>
          }
        />
        <Route
          path="clockin"
          element={
            <PageTransition>
              <ClockInClockOut />
            </PageTransition>
          }
        />
        <Route
          path="ComingSoon"
          element={
            <PageTransition>
              <ComingSoon />
            </PageTransition>
          }
        />
        <Route
          path="core-hr/employee-database"
          element={
            <PageTransition>
              <EmployeeDatabase />
            </PageTransition>
          }
        />
        <Route
          path="employees"
          element={
            <PageTransition>
              <EmployeeDatabase />
            </PageTransition>
          }
        />
        <Route
          path="employees/create"
          element={
            <PageTransition>
              <EmployeeFormPage />
            </PageTransition>
          }
        />
        <Route
          path="employees/:id/edit"
          element={
            <PageTransition>
              <EmployeeFormPage />
            </PageTransition>
          }
        />
        <Route
          path="core-hr/digital-files"
          element={
            <PageTransition>
              <DigitalFiles />
            </PageTransition>
          }
        />
        <Route
          path="core-hr/rbac"
          element={
            <PageTransition>
              <RBAC />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/requisitions"
          element={
            <PageTransition>
              <JobRequisitions />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/requisitions/create"
          element={
            <PageTransition>
              <JobRequisitionForm />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/requisitions/:requisitionId"
          element={
            <PageTransition>
              <JobRequisitionDetailPage />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/requisitions/:requisitionId/edit"
          element={
            <PageTransition>
              <JobRequisitionEditPage />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/integrations"
          element={
            <PageTransition>
              <SourcingIntegrations />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/ats-dashboard"
          element={
            <PageTransition>
              <ATSDashboard />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/ats"
          element={
            <PageTransition>
              <ATSDashboard />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/ats/:requisitionId"
          element={
            <PageTransition>
              <ApplicationReview />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/applications/:applicationId"
          element={
            <PageTransition>
              <ApplicationDetails />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/offers"
          element={
            <PageTransition>
              <Offers />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/offers/approvals"
          element={
            <PageTransition>
              <OfferApprovalsPage />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/offers/edit/:id"
          element={
            <PageTransition>
              <Offers />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/preboarding"
          element={
            <PageTransition>
              <PreBoardingDashboard />
            </PageTransition>
          }
        />
        <Route
          path="recruiting/preboarding/:packetId"
          element={
            <PageTransition>
              <DocumentVerificationPage />
            </PageTransition>
          }
        />
        <Route
          path="preboarding/:offerId"
          element={
            <PageTransition>
              <CandidatePortal />
            </PageTransition>
          }
        />
        <Route
          path="payroll/run"
          element={
            <PageTransition>
              <PayrollPage />
            </PageTransition>
          }
        />
        <Route
          path="analytics/headcount"
          element={
            <PageTransition>
              <AnalyticsPage />
            </PageTransition>
          }
        />

        {/* Core HR - Additional Routes */}
        <Route
          path="core-hr/ids"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />

        {/* Recruitment - Additional Routes */}
        <Route
          path="recruiting/resume-db"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="onboarding/checklists"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />

        {/* Attendance & Leave Routes */}
        <Route
          path="attendance/biometric"
          element={
            <PageTransition>
              <DevicesList />
            </PageTransition>
          }
        />
        <Route
          path="attendance/biometric/:id"
          element={
            <PageTransition>
              <DeviceDetails />
            </PageTransition>
          }
        />
        <Route
          path="schedule"
          element={
            <PageTransition>
              <AttendancePage defaultTab="shift" />
            </PageTransition>
          }
        />
        <Route
          path="attendance/geo"
          element={
            <PageTransition>
              <AttendancePage defaultTab="geofence" />
            </PageTransition>
          }
        />
        <Route
          path="attendance/shifts"
          element={
            <PageTransition>
              <AttendancePage defaultTab="shift" />
            </PageTransition>
          }
        />
        <Route
          path="attendance/overtime"
          element={
            <PageTransition>
              <AttendancePage defaultTab="report" />
            </PageTransition>
          }
        />
        <Route
          path="leave/requests"
          element={
            <PageTransition>
              <LeaveRequest />
            </PageTransition>
          }
        />
        <Route
          path="leave/policies"
          element={
            <PageTransition>
              <LeaveApproval defaultTab="policies" />
            </PageTransition>
          }
        />
        <Route
          path="leave/holidays"
          element={
            <PageTransition>
              <LeaveApproval defaultTab="holidays" />
            </PageTransition>
          }
        />

        {/* Procurement Routes */}
        <Route
          path="procurement"
          element={
            <PageTransition>
              <RequestList />
            </PageTransition>
          }
        />
        <Route
          path="procurement/requests"
          element={
            <PageTransition>
              <RequestList />
            </PageTransition>
          }
        />
        <Route
          path="procurement/requests/create"
          element={
            <PageTransition>
              <RequestCreate />
            </PageTransition>
          }
        />
        <Route
          path="procurement/requests/:id"
          element={
            <PageTransition>
              <RequestDetail />
            </PageTransition>
          }
        />
        <Route
          path="procurement/requests/:id/edit"
          element={
            <PageTransition>
              <RequestEdit />
            </PageTransition>
          }
        />
        <Route
          path="procurement/approvals"
          element={
            <PageTransition>
              <ApprovalList />
            </PageTransition>
          }
        />
        <Route
          path="procurement/workflows"
          element={
            <PageTransition>
              <WorkflowList />
            </PageTransition>
          }
        />
        <Route
          path="procurement/workflows/create"
          element={
            <PageTransition>
              <WorkflowCreate />
            </PageTransition>
          }
        />
        <Route
          path="procurement/workflows/:id/edit"
          element={
            <PageTransition>
              <WorkflowEdit />
            </PageTransition>
          }
        />
        <Route
          path="procurement/organization-structure"
          element={
            <PageTransition>
              <OrganizationalStructureMaintenance />
            </PageTransition>
          }
        />
        <Route
          path="procurement/memo"
          element={
            <PageTransition>
              <InterOfficeMemo />
            </PageTransition>
          }
        />
        <Route
          path="procurement/InterOfficeMemo"
          element={
            <PageTransition>
              <InterOfficeMemo />
            </PageTransition>
          }
        />
        <Route
          path="procurement/vendors"
          element={
            <PageTransition>
              <VendorList />
            </PageTransition>
          }
        />

        {/* Payroll Routes */}
        <Route
          path="payroll/compensations/:id"
          element={
            <PageTransition>
              <CompensationAssignmentDetailPage />
            </PageTransition>
          }
        />
        <Route
          path="payroll/ctc/:id"
          element={
            <PageTransition>
              <SalaryStructureDetailPage />
            </PageTransition>
          }
        />
        <Route
          path="payroll/ctc"
          element={
            <PageTransition>
              <PayrollPage />
            </PageTransition>
          }
        />
        <Route
          path="pay"
          element={
            <PageTransition>
              <PayrollPage />
            </PageTransition>
          }
        />
        <Route
          path="payroll/statutory"
          element={
            <PageTransition>
              <PayrollPage />
            </PageTransition>
          }
        />
        <Route
          path="payroll/benefits"
          element={
            <PageTransition>
              <PayrollPage />
            </PageTransition>
          }
        />
        <Route
          path="benefits"
          element={
            <PageTransition>
              <PayrollPage />
            </PageTransition>
          }
        />
        <Route
          path="payroll/fnf"
          element={
            <PageTransition>
              <PayrollPage />
            </PageTransition>
          }
        />
        <Route
          path="payroll/form16"
          element={
            <PageTransition>
              <PayrollPage />
            </PageTransition>
          }
        />
        <Route
          path="payroll/bank"
          element={
            <PageTransition>
              <PayrollPage />
            </PageTransition>
          }
        />
        <Route
          path="payroll/reports"
          element={
            <PageTransition>
              <PayrollPage />
            </PageTransition>
          }
        />

        {/* Performance Routes */}
        <Route
          path="performance/goals"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="performance/feedback"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="performance/360"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="performance/competencies"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="performance/reviews"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />

        {/* ESS Portal Routes */}
        <Route
          path="ess/payslips"
          element={
            <PageTransition>
              <PayrollPage />
            </PageTransition>
          }
        />
        <Route
          path="ess/leave-attendance"
          element={
            <PageTransition>
              <LeaveRequest />
            </PageTransition>
          }
        />
        <Route
          path="ess/investments"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="ess/policies"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="ess/claims"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />

        {/* Learning & Development Routes */}
        <Route
          path="lnd/tni"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="lnd/courses"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="lnd/scorm"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="lnd/certifications"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="lnd/skills"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />

        {/* Expense & Travel Routes */}
        <Route
          path="expense-travel/requests"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="expense-travel/ocr"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="expense-travel/payroll-link"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="expense-travel/policies"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />

        {/* Exit Management Routes */}
        <Route
          path="exit/resignations"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="exit/interviews"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="exit/kt"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="exit/clearance"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="exit/fnf-payroll"
          element={
            <PageTransition>
              <PayrollPage />
            </PageTransition>
          }
        />

        {/* Compliance & Legal Routes */}
        <Route
          path="compliance/se-act"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="compliance/min-wage-ot"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="compliance/ida"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="compliance/maternity"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="compliance/posh"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />

        {/* Analytics Routes */}
        <Route
          path="analytics/attrition"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="analytics/diversity"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="analytics/payroll-cost"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="analytics/leave-trends"
          element={
            <PageTransition>
              <AttendancePage defaultTab="report" />
            </PageTransition>
          }
        />
        <Route
          path="analytics/mis"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />

        {/* Integrations & Tech Routes */}
        <Route
          path="tech/mobile"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="tech/api"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="tech/chatbot"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="tech/notifications"
          element={
            <PageTransition>
              <TechNotifications />
            </PageTransition>
          }
        />
        <Route
          path="tech/documents"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="tech/dashboards"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />

        {/* Advanced Routes */}
        <Route
          path="advanced/ai-screening"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="advanced/predictive-attrition"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="advanced/sentiment"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="advanced/workforce-planning"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />
        <Route
          path="advanced/succession"
          element={
            <PageTransition>
              <PlaceholderPage />
            </PageTransition>
          }
        />

        {/* Terminal / Server Logs */}
        <Route
          path="terminal"
          element={
            <PageTransition>
              <TerminalLogs />
            </PageTransition>
          }
        />

        {/* EDOS Routes */}
        <Route
          path="edos/projects"
          element={
            <PageTransition>
              <ProjectsPage />
            </PageTransition>
          }
        />
        <Route
          path="edos/projects/create"
          element={
            <PageTransition>
              <ProjectCreatePage />
            </PageTransition>
          }
        />
        <Route
          path="edos/projects/:id"
          element={
            <PageTransition>
              <ProjectDetailPage />
            </PageTransition>
          }
        />
        <Route
          path="edos/sprints"
          element={
            <PageTransition>
              <SprintsPage />
            </PageTransition>
          }
        />
        <Route
          path="edos/sprints/create"
          element={
            <PageTransition>
              <SprintCreatePage />
            </PageTransition>
          }
        />
        <Route
          path="edos/sprints/:id"
          element={
            <PageTransition>
              <SprintDetailPage />
            </PageTransition>
          }
        />
        <Route
          path="edos/dashboards"
          element={
            <PageTransition>
              <EDOSDashboardsPage />
            </PageTransition>
          }
        />
        <Route
          path="edos/milestones"
          element={
            <PageTransition>
              <MilestonesPage />
            </PageTransition>
          }
        />
        <Route
          path="edos/milestones/create"
          element={
            <PageTransition>
              <MilestoneCreatePage />
            </PageTransition>
          }
        />
        <Route
          path="edos/milestones/:id"
          element={
            <PageTransition>
              <MilestoneDetailPage />
            </PageTransition>
          }
        />
        <Route
          path="edos/releases"
          element={
            <PageTransition>
              <ReleasesPage />
            </PageTransition>
          }
        />
        <Route
          path="edos/releases/create"
          element={
            <PageTransition>
              <ReleaseCreatePage />
            </PageTransition>
          }
        />
        <Route
          path="edos/releases/:id"
          element={
            <PageTransition>
              <ReleaseDetailPage />
            </PageTransition>
          }
        />
        <Route
          path="edos/timesheets"
          element={
            <PageTransition>
              <TimesheetsPage />
            </PageTransition>
          }
        />
        <Route
          path="edos/timesheets/create"
          element={
            <PageTransition>
              <TimesheetCreatePage />
            </PageTransition>
          }
        />
        <Route
          path="edos/resources"
          element={
            <PageTransition>
              <ResourcesPage />
            </PageTransition>
          }
        />
        <Route
          path="edos/portfolios"
          element={
            <PageTransition>
              <PortfoliosPage />
            </PageTransition>
          }
        />
        <Route
          path="edos/programs"
          element={
            <PageTransition>
              <ProgramsPage />
            </PageTransition>
          }
        />
        <Route
          path="edos/automations"
          element={
            <PageTransition>
              <AutomationsPage />
            </PageTransition>
          }
        />

        <Route
          path="*"
          element={
            <PageTransition>
              <NotFound />
            </PageTransition>
          }
        />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
